import Run, { DateRuns, OrdenRun } from '../types/run';
import Game from '../types/game';
import { STATUS } from './constants';

export const successRunFilter = (run: Run): boolean => {
  return (!run.includesHit);
};

export const calculateSuccessRate = ({ successfulRuns, totalRuns }: { successfulRuns: Run[], totalRuns: Run[] }): number => {
  if (!successfulRuns || totalRuns.length === 0) {
    return 0;
  }
  return Math.round((successfulRuns.length / totalRuns.length) * 10000) / 100;
};

/* 
  input: 
   - xs: [{ date: 11-02-2021 }, { date: 11-02-2021 }, { date: 12-02-2021 }, { date: 13-02-2021 }, { date: 13-02-2021 }]
   - key: 'splitHit'
  output: {"11-02-2021":[{"date":"11-02-2021"},{"date":"11-02-2021"}],"12-02-2021":[{"date":"12-02-2021"}],"13-02-2021":[{"date":"13-02-2021"},{"date":"13-02-2021"}]}
*/
export const groupBy = function (xs: [], key: string) {
  return xs.reduce(function (rv, x) {
    ((rv[x[key]] = rv[x[key]] || []) as []).push(x);
    return rv;
  }, {});
};

/**
 * This is required to avoid issues with the dates on West Countries as the
 * format we are using: YYYY-MM-DD can be translated to Date but would be
 * that date in GMT so we need to set it up to noon to avoid issues
 * TODO: avoid save dates in strings and use timestamp as should be :(
 */
export const addTimePadding = (stringDate: string) => {
  return `${stringDate}T12:00:00`;
};

export const stringDateToDate = (stringDate: string) => {
  return new Date(addTimePadding(stringDate));
};


export const formatDate = (date: Date | string): string => {
  if(typeof date === 'string') {
    date = addTimePadding(date);
  }
  var d = new Date(date),
      month = '' + (d.getMonth() + 1),
      day = '' + d.getDate(),
      year = d.getFullYear();

  if (month.length < 2) 
      month = '0' + month;
  if (day.length < 2) 
      day = '0' + day;

  return [year, month, day].join('-');
};

export const groupByDateAcc = (acc: DateRuns[], run: Run) => {
  const dateExists: DateRuns | undefined = acc.find((item: DateRuns) => {
    return item.date === run.date
  });
  if (dateExists) {
    dateExists.runs.push(run);
  } else {
    acc.push({ 'date': run.date, runs: [run] })
  }
  return acc;
};

export const sortByDate = (run1: DateRuns, run2: DateRuns) => {
  if (!run1.date || !run2.date) {
    return 1;
  }
  return (+new Date(run1.date) - +new Date(run2.date))
};

const isNGame = ({ run, index, runs, previousRuns }: { run: Run, index: number, runs: Run[], previousRuns: number }) => {
  if(previousRuns === 0 && run.reset) {
    return true; // When run with reset, is First run
  }
  // Run is minor that the current number (it is 3th run and we check for game 3, 4, 5, 6)
  if (index < previousRuns) {
    return false;
  }
  // Reset run
  if (previousRuns > 0 && run.reset) {
    return false;
  }
  // Previous + 1 as it will also be the next type
  if (runs[index - (previousRuns + 1)] && runs[index - (previousRuns + 1)].includesHit === false) {
    return false;
  }
  let isNGame = true;
  for (let i = 0, j = 1; i < previousRuns; i += 1, j += 1) {
    if ((runs[index - j] && runs[index - j].includesHit === true)) {
      isNGame = false;
    }
  }
  return isNGame;
};

export const getRunsByGameNumber = ({ runs, initialRuns, games } : { runs: Run[], initialRuns?: Run[], games: Game[] }) => {
  let runsByGameNumber: Run[][] = [];
  games.forEach(() => runsByGameNumber.push([] as Run[]));
  const runsToUse = initialRuns || runs;
  const clearedRuns = runsToUse.filter((run) => run.endSplit === undefined);
  clearedRuns.forEach((run, index) => {
    games.forEach((game, gameIndex) => {
      const checkIfRunIsIndexGame = isNGame({ run, index, runs: clearedRuns, previousRuns: gameIndex });
      if(checkIfRunIsIndexGame) {
        if(initialRuns && runs.includes(run)) {
          runsByGameNumber[gameIndex].push(run);
        } else if(!initialRuns) {
          runsByGameNumber[gameIndex].push(run);
        }
      }
    });
  });
  return runsByGameNumber;
};

export const getDistancePB = (runs: Run[], games: Game[]): OrdenRun => {
  const runsByGameNumber = getRunsByGameNumber({ runs, games });
  let distancePB : OrdenRun = {
    gameNumber: 1,
    splitName: '',
    clip: '',
  } ;
  const runsWithContent = runsByGameNumber.filter(gameOrderRuns => gameOrderRuns.length > 0);
  distancePB.gameNumber = runsWithContent.length;
  const runsForLatestGames = runsWithContent[runsWithContent.length-1];
  const sortedLastGameRuns = runsForLatestGames.sort((runA, runB) => {
    const gameBSplits = games[runB.selectedGame].splits.length;
    const gameASplits = games[runA.selectedGame].splits.length;
    const runBPercentage: number = ((runB.splitHit || gameBSplits) / gameBSplits) * 100;
    const runAPercentage: number = ((runA.splitHit || gameASplits) / gameASplits) * 100;
    return runBPercentage - runAPercentage;
  });
  const runWithPB = sortedLastGameRuns[0];
  const nextRunIndex = runs.findIndex(run => run._id === runWithPB._id) + 1;
  const nextRun = runs[nextRunIndex];
  const nextRunIsReset = nextRun?.reset;
  if(nextRunIsReset) {
    distancePB.splitName = `Run Resetted after beating game ${distancePB.gameNumber}`;
    distancePB.clip = sortedLastGameRuns[0].clip || '';
  } else {
    distancePB.splitName = runWithPB.splitHit !== undefined || runWithPB.splitHit === 0 ? games[runWithPB.selectedGame]?.splits?.[runWithPB.splitHit].name : 'The Run is DONE!!!!';
    distancePB.clip = sortedLastGameRuns[0].clip || '';
  }
  return distancePB;
};

export const sortRuns = (run1: Run, run2: Run) => {
  const run1Date = run1.date;
  const run2Date = run2.date;
  let run1DateOrder: number = 0, run2DateOrder: number = 0;
  if (run1Date) {
    run1DateOrder = new Date(run1Date).getTime() + run1.order;
  }
  if (run2Date) {
    run2DateOrder = new Date(run2Date).getTime() + run2.order;
  }
  return run1DateOrder - run2DateOrder;
};

export const getRunStatus = (run: Run, games: Game[]) => {
  const runGame = games[run.selectedGame];
  const finalGameSplit = runGame.splits.length;
  if (run.partial && (run.endSplit && run.endSplit < finalGameSplit)) {
    return STATUS.NOTENDED;
  }
  return (run.includesHit) ? STATUS.FAILED : STATUS.SUCCEEDED;
};

export const sortByOrder = ((splitA: any, splitB: any) => (splitA.order - splitB.order));


